import React, { useMemo, useRef, useState } from "react";

export type CategoryValue =
  | { kind: "existing"; id: string; name: string }
  | { kind: "new"; name: string }
  | null;

interface CategoryComboboxProps {
  categories: { id: string; name: string }[];
  value: CategoryValue;
  onChange: (value: CategoryValue) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Search-or-add combobox over GET /scenarios/categories.
 *
 * - Type freely; the dropdown filters case-insensitively.
 * - If the typed text exactly matches an existing category (CI), arrow-down +
 *   Enter selects it (kind: 'existing').
 * - If it doesn't match, the bottom of the dropdown surfaces a `Create
 *   "<typed>"` option that emits (kind: 'new', name).
 * - Keyboard: ArrowUp / ArrowDown navigates, Enter selects, Escape clears
 *   focus.
 *
 * Intentionally NO external combobox dependency. Reuses LibraryPage Tailwind
 * tokens (surface-container, on-surface, primary, outline-variant).
 */
const CategoryCombobox: React.FC<CategoryComboboxProps> = ({
  categories,
  value,
  onChange,
  placeholder = "Type a category…",
  disabled = false,
}) => {
  const [query, setQuery] = useState<string>(value?.name ?? "");
  const [open, setOpen] = useState<boolean>(false);
  const [highlight, setHighlight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep the input in sync with the bound value when it changes externally.
  React.useEffect(() => {
    if (value) {
      setQuery(value.name);
    }
  }, [value?.kind, value?.name]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, query]);

  const exactMatch = useMemo(
    () =>
      categories.find(
        (c) => c.name.toLowerCase() === query.trim().toLowerCase(),
      ),
    [categories, query],
  );

  const showCreate =
    query.trim().length > 0 && !exactMatch;

  // The full option list including the synthetic "Create" entry.
  const options: (
    | { kind: "existing"; id: string; name: string }
    | { kind: "create"; name: string }
  )[] = [
    ...filtered.map((c) => ({ kind: "existing" as const, id: c.id, name: c.name })),
    ...(showCreate ? [{ kind: "create" as const, name: query.trim() }] : []),
  ];

  const select = (idx: number) => {
    const opt = options[idx];
    if (!opt) return;
    if (opt.kind === "existing") {
      onChange({ kind: "existing", id: opt.id, name: opt.name });
      setQuery(opt.name);
    } else {
      onChange({ kind: "new", name: opt.name });
      setQuery(opt.name);
    }
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && options.length > 0) {
        select(highlight);
      } else if (query.trim().length > 0) {
        // Plain Enter without dropdown still commits the typed value as new.
        if (exactMatch) {
          onChange({ kind: "existing", id: exactMatch.id, name: exactMatch.name });
        } else {
          onChange({ kind: "new", name: query.trim() });
        }
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
          // Clear the bound value when the user starts editing.
          if (value) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-surface-container rounded-md px-3 py-2 text-on-surface font-body text-sm border border-outline-variant/30 focus:outline-none focus:border-primary disabled:opacity-50"
      />
      {open && options.length > 0 && (
        <ul className="absolute left-0 right-0 mt-1 z-20 bg-surface-container-high rounded-md border border-outline-variant/30 shadow-lg max-h-60 overflow-y-auto">
          {options.map((opt, idx) => {
            const isHi = idx === highlight;
            const label =
              opt.kind === "create" ? `Create "${opt.name}"` : opt.name;
            return (
              <li key={`${opt.kind}-${"id" in opt ? opt.id : opt.name}`}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    // mousedown so the input doesn't blur first
                    e.preventDefault();
                    select(idx);
                  }}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`w-full text-left px-3 py-2 text-sm font-body ${
                    isHi
                      ? "bg-primary/15 text-on-surface"
                      : "text-on-surface hover:bg-surface-container-highest"
                  } ${opt.kind === "create" ? "italic text-primary" : ""}`}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CategoryCombobox;
